//src\PO-Process\components\ItemNameEditor.jsx

import { forwardRef, useEffect, useImperativeHandle, useState } from "react";
import { getItemNames, updatePOData,updateSkuMapping } from "../api/api";
import { Autocomplete, TextField } from "@mui/material";
import { Toaster } from "../../components/Toaster";
import { getUser } from "../../helpers";

const ItemNameEditor = forwardRef((props, ref) => {
    const [options, setOptions] = useState([]);
    const [inputValue, setInputValue] = useState(props.value || ""); // Input value to control what user types
    const [value, setValue] = useState(props.value || ""); // Value to control selected option

    const { rowIndex, context } = props;
    
    const user = getUser();

    useEffect(() => {
        const fetchOptions = async () => {
            try {
                const data = await getItemNames();
                
                // Get all item names already present in the table (except current row)
                const existingNames = (context.poData?.["Table Body"] || [])
                    .filter((_, idx) => idx !== rowIndex)   // exclude current row
                    .map(row => row["Item Name"])
                    .filter(Boolean); // remove null/empty
                            
                // Filter dropdown options
                const filtered = data.filter(item => !existingNames.includes(item));

                // Add the current row’s existing value back (in case it's removed)
                if (props.value && !filtered.includes(props.value)) {
                    filtered.push(props.value);
                }

                setOptions(filtered);

            } catch (error) {
                console.error("Failed to load item names", error);
                Toaster.error("Failed to load item names", error);
            }
        };
        fetchOptions();
    }, []);

    useEffect(() => {
        // Reset the input and value if the props.value changes
        setInputValue(props.value || "");
        setValue(props.value || "");
    }, [props.value]);

    useImperativeHandle(ref, () => ({
        getValue() {
            return inputValue;
        },
        isPopup() {
            return true;
        },
        isCancelAfterEnd() {
            return false;
        }
    }));

    const handleSelect = async (selected) => {
        if (!selected) {
            console.warn("No item selected.");
            Toaster.warning("No item selected.")
            return;
        }

        setInputValue(selected);
        setValue(selected);

        const matchedSKU = context.skuMaster.find(
            (item) =>
                typeof item?.Product_Name === "string" &&
                item.Product_Name.toLowerCase() === selected.toLowerCase()
        );

        const sdCode = matchedSKU ? matchedSKU.SKU_ID : "";

        try {
            await updatePOData({
                type: "item",
                filename: context.filename,
                old_label: "Item Name",
                label: "Item Name",
                new_value: selected,
                rowIndex,
                user
            });

            if (sdCode) {
                await updatePOData({
                    type: "item",
                    filename: context.filename,
                    old_label: "SD_Code",
                    label: "SD_Code",
                    new_value: sdCode,
                    rowIndex,
                    user
                });
            }

            context.setPoData((prevPoData) => {
                const updatedTable = [...(prevPoData["Table Body"] || [])];
                updatedTable[rowIndex] = {
                    ...updatedTable[rowIndex],
                    ["Item Name"]: selected,
                    SD_Code: sdCode,
                };
                return {
                    ...prevPoData,
                    "Table Body": updatedTable,
                };
            });

            const rowData = context.poData["Table Body"][rowIndex];
            const itemCode = rowData?.["Item Code"];

            if (context.poData?.Customer && itemCode && sdCode) {
                await updateSkuMapping({
                    Platform: context.poData.Customer,
                    Item_Code: itemCode,
                    SD_Code: sdCode,
                });
                console.log("sku_code_mapping updated:", itemCode, context.poData.Customer, sdCode);
                Toaster.success("sku_code_mapping updated:", itemCode, context.poData.Customer, sdCode);
            } else {
                console.warn("Missing data for sku_code_mapping update:", itemCode, context.poData.Customer, sdCode);
                Toaster.warning("Missing data for sku_code_mapping update:", itemCode, context.poData.Customer, sdCode)
            }

            console.log("Item Name & SD_Code updated:", selected, sdCode);
            Toaster.success("Item Name & SD_Code updated:", selected, sdCode);
        } catch (err) {
            console.error("Error updating Item Name or SD_Code:", err);
            Toaster.error("Error updating Item Name or SD_Code:", err);
        }

        requestAnimationFrame(() => {
            if (props.stopEditing) props.stopEditing();
        });
    };

    return (
        <Autocomplete
            options={options}
            getOptionLabel={(option) => option}
            value={value} // Set selected value to pre-select previous value
            inputValue={inputValue} // Controlled input value (what user types)
            onInputChange={(event, newInputValue) => {
                setInputValue(newInputValue); // Update input value as user types
            }}
            onChange={(event, newValue) => {
                if (newValue) {
                    handleSelect(newValue); // Handle value selection
                }
            }}
            renderInput={(params) => (
                <TextField
                    {...params}
                    variant="outlined"
                    size="small"
                    placeholder="Select Item Name"
                    autoFocus
                />
            )}
            disableClearable
            openOnFocus
            size="small"
            fullWidth
        />
    );
});

export default ItemNameEditor;
